import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { AuthService } from '../../../services/auth.service';
import { ProductService } from '../../../services/product.service';
import { OrderService } from '../../../services/order.service';
import { AdminOrderService } from '../../../services/admin-order.service';
import { User } from '../../../interfaces/product.interface';
import { Subject, takeUntil } from 'rxjs';
import { collection, getDocs, getFirestore } from '@angular/fire/firestore';
import { ToastService } from '../../../services/toast.service';

@Component({
  selector: 'app-admin-dashboard',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './admin-dashboard.html',
  styleUrls: ['./admin-dashboard.scss']
})
export class AdminDashboardComponent implements OnInit, OnDestroy {
  currentUser: User | null = null;
  dashboardStats = {
    totalOrders: 0,
    totalProducts: 0,
    totalUsers: 0,
    totalRevenue: 0,
    pendingOrders: 0,
    completedOrders: 0,
    lowStockProducts: 0,
    // Previous month data for growth calculations
    previousMonthOrders: 0,
    previousMonthRevenue: 0,
    previousMonthUsers: 0
  };

  recentOrders: any[] = [];
  topProducts: any[] = [];
  isLoading = true;
  hasError = false;
  errorMessage = '';
  isSuperAdmin = false;
  Math = Math; // Make Math available in template
  ordersGraphData = {
    pending: 0,
    shipped: 0,
    delivered: 0
  };

  private destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private productService: ProductService,
    private orderService: OrderService,
    private adminOrderService: AdminOrderService,
    private toastService: ToastService
  ) {}

  ngOnInit() {
    this.authService.currentUser$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(user => {
      this.currentUser = user;
      this.isSuperAdmin = user?.role === 'super_admin';
    });

    this.loadDashboardData();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadDashboardData() {
    try {
      this.isLoading = true;
      this.hasError = false;
      this.errorMessage = '';
      
      // Load dashboard statistics
      await this.loadDashboardStats();
      
      // Load recent orders
      await this.loadRecentOrders();
      
      // Load top products (only for super admin)
      if (this.isSuperAdmin) {
      await this.loadTopProducts();
      }
      
      // Load orders graph data (for regular admins)
      if (!this.isSuperAdmin) {
        await this.loadOrdersGraphData();
      }
      
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      this.hasError = true;
      this.errorMessage = 'Failed to load dashboard data. Please try again.';
      this.toastService.error('Failed to load dashboard data. Please try again.');
    } finally {
      this.isLoading = false;
    }
  }

  private async loadDashboardStats() {
    try {
      // Get order statistics from admin service
      this.adminOrderService.getOrderStats().pipe(
        takeUntil(this.destroy$)
      ).subscribe({
        next: (stats) => {
          this.dashboardStats.totalOrders = stats.totalOrders;
          this.dashboardStats.pendingOrders = stats.pendingOrders;
          this.dashboardStats.completedOrders = stats.deliveredOrders;
          this.dashboardStats.totalRevenue = stats.totalRevenue;
          
          // Calculate previous month data (simplified - in real app, you'd fetch this from database)
          this.calculatePreviousMonthData();
        },
        error: (error) => {
          console.error('Error loading order stats:', error);
          this.hasError = true;
          this.errorMessage = 'Failed to load order statistics.';
        }
      });

      // Get product count
      this.productService.getProducts().pipe(
        takeUntil(this.destroy$)
      ).subscribe({
        next: (products) => {
          this.dashboardStats.totalProducts = products.length;
          // Count low stock products (products that are not in stock)
          this.dashboardStats.lowStockProducts = products.filter(p => !p.inStock).length;
        },
        error: (error) => {
          console.error('Error loading products:', error);
          this.hasError = true;
          this.errorMessage = 'Failed to load product data.';
        }
      });

      // Get user count from Firestore
      this.getUserCount().then(count => {
        this.dashboardStats.totalUsers = count;
      }).catch(error => {
        console.error('Error loading user count:', error);
        this.hasError = true;
        this.errorMessage = 'Failed to load user data.';
      });
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
      this.hasError = true;
      this.errorMessage = 'Failed to load dashboard statistics.';
    }
  }

  private async loadRecentOrders() {
    try {
      // Get recent orders from admin service
      this.adminOrderService.getAdminOrders(5).pipe(
        takeUntil(this.destroy$)
      ).subscribe(result => {
        this.recentOrders = result.orders.slice(0, 5).map(order => ({
          id: order.id,
          customerName: order.userName || 'Unknown User',
          amount: order.totalAmount,
          status: order.status,
          date: order.orderDate?.toDate() || new Date()
        }));
      });
    } catch (error) {
      console.error('Error loading recent orders:', error);
    }
  }

  private async loadTopProducts() {
    try {
      // Get products and show them based on price (as a proxy for popularity)
      this.productService.getProducts().pipe(
        takeUntil(this.destroy$)
      ).subscribe(products => {
        // Sort products by price (assuming higher price = more premium = potentially more popular)
        // Price is now optional, so filter out products without price or use 0 as fallback
        const sortedProducts = products
          .filter(p => p.isActive && (p.price !== undefined && p.price !== null))
          .sort((a, b) => (b.price ?? 0) - (a.price ?? 0))
          .slice(0, 3);
        
        this.topProducts = sortedProducts.map(product => ({
          name: product.name,
          sales: Math.floor(Math.random() * 30) + 5, // Placeholder sales data
          revenue: (product.price ?? 0) * (Math.floor(Math.random() * 30) + 5)
        }));
      });
    } catch (error) {
      console.error('Error loading top products:', error);
    }
  }

  private async loadOrdersGraphData() {
    try {
      // Get order statistics for the graph
      this.adminOrderService.getOrderStats().pipe(
        takeUntil(this.destroy$)
      ).subscribe(stats => {
        this.ordersGraphData = {
          pending: stats.pendingOrders,
          shipped: stats.shippedOrders || 0,
          delivered: stats.deliveredOrders
        };
      });
    } catch (error) {
      console.error('Error loading orders graph data:', error);
    }
  }

  getStatusColor(status: string): string {
    const statusColors: { [key: string]: string } = {
      'pending': '#ffc107',
      'confirmed': '#17a2b8',
      'shipped': '#007bff',
      'delivered': '#28a745',
      'cancelled': '#dc3545'
    };
    return statusColors[status] || '#6c757d';
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  }

  formatDate(date: Date): string {
    return new Intl.DateTimeFormat('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(date);
  }

  getLast7DaysRange(): string {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 6);
    
    return `${startDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} - ${endDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}`;
  }

  getBarHeight(value: number): string {
    const maxValue = Math.max(this.ordersGraphData.pending, this.ordersGraphData.shipped, this.ordersGraphData.delivered);
    if (maxValue === 0) return '20px';
    const percentage = (value / maxValue) * 100;
    return `${Math.max(20, percentage)}%`;
  }

  refreshDashboard() {
    this.toastService.info('Refreshing dashboard data...');
    this.loadDashboardData();
  }

  testToasts() {
    this.toastService.success('This is a success toast!');
    setTimeout(() => {
      this.toastService.error('This is an error toast!');
    }, 1000);
    setTimeout(() => {
      this.toastService.warning('This is a warning toast!');
    }, 2000);
    setTimeout(() => {
      this.toastService.info('This is an info toast!');
    }, 3000);
  }

  // Dynamic status methods for stat-change data
  getOrderGrowthPercentage(): number {
    // Calculate growth based on current vs previous month
    if (this.dashboardStats.previousMonthOrders === 0) return 0;
    const growth = ((this.dashboardStats.totalOrders - this.dashboardStats.previousMonthOrders) / this.dashboardStats.previousMonthOrders) * 100;
    return Math.round(growth);
  }

  getRevenueGrowthPercentage(): number {
    // Calculate revenue growth percentage
    if (this.dashboardStats.previousMonthRevenue === 0) return 0;
    const growth = ((this.dashboardStats.totalRevenue - this.dashboardStats.previousMonthRevenue) / this.dashboardStats.previousMonthRevenue) * 100;
    return Math.round(growth);
  }

  getUserGrowthPercentage(): number {
    // Calculate user growth percentage
    if (this.dashboardStats.previousMonthUsers === 0) return 0;
    const growth = ((this.dashboardStats.totalUsers - this.dashboardStats.previousMonthUsers) / this.dashboardStats.previousMonthUsers) * 100;
    return Math.round(growth);
  }

  getProductChangeStatus(): string {
    // Dynamic product change status
    if (this.dashboardStats.totalProducts === 0) return 'No products';
    if (this.dashboardStats.totalProducts < 5) return 'Growing inventory';
    if (this.dashboardStats.totalProducts < 15) return 'Stable inventory';
    return 'Well stocked';
  }

  getPendingOrdersStatus(): string {
    // Dynamic pending orders status
    if (this.dashboardStats.pendingOrders === 0) return 'All caught up';
    if (this.dashboardStats.pendingOrders <= 5) return 'Low priority';
    if (this.dashboardStats.pendingOrders <= 15) return 'Needs attention';
    return 'High priority';
  }

  getCompletedOrdersStatus(): number {
    // Calculate completed orders growth
    if (this.dashboardStats.previousMonthOrders === 0) return 0;
    const previousCompleted = Math.floor(this.dashboardStats.previousMonthOrders * 0.8); // Assume 80% completion rate
    if (previousCompleted === 0) return 0;
    const growth = ((this.dashboardStats.completedOrders - previousCompleted) / previousCompleted) * 100;
    return Math.round(growth);
  }

  getLowStockStatus(): string {
    // Dynamic low stock status
    if (this.dashboardStats.lowStockProducts === 0) return 'All stocked';
    if (this.dashboardStats.lowStockProducts <= 3) return 'Minor restock needed';
    if (this.dashboardStats.lowStockProducts <= 8) return 'Restock needed';
    return 'Major restock needed';
  }

  getSuccessRateStatus(): string {
    // Dynamic success rate status
    const successRate = this.dashboardStats.totalOrders > 0 ? 
      (this.dashboardStats.completedOrders / this.dashboardStats.totalOrders) * 100 : 0;
    
    if (successRate >= 90) return 'Excellent performance';
    if (successRate >= 80) return 'High performance';
    if (successRate >= 70) return 'Good performance';
    if (successRate >= 60) return 'Average performance';
    return 'Needs improvement';
  }

  getStockHealthStatus(): string {
    // Dynamic stock health status
    const stockHealth = this.dashboardStats.totalProducts > 0 ? 
      ((this.dashboardStats.totalProducts - this.dashboardStats.lowStockProducts) / this.dashboardStats.totalProducts) * 100 : 0;
    
    if (stockHealth >= 90) return 'Excellent health';
    if (stockHealth >= 80) return 'Very good health';
    if (stockHealth >= 70) return 'Good health';
    if (stockHealth >= 60) return 'Fair health';
    return 'Poor health';
  }

  private calculatePreviousMonthData() {
    // Calculate previous month data based on current data
    // In a real application, you would fetch this data from the database
    // For now, we'll simulate realistic previous month data
    
    // Previous month orders (assume some growth)
    this.dashboardStats.previousMonthOrders = Math.max(1, Math.floor(this.dashboardStats.totalOrders * 0.85));
    
    // Previous month revenue (assume some growth)
    this.dashboardStats.previousMonthRevenue = Math.max(1000, Math.floor(this.dashboardStats.totalRevenue * 0.88));
    
    // Previous month users (assume some growth)
    this.dashboardStats.previousMonthUsers = Math.max(1, Math.floor(this.dashboardStats.totalUsers * 0.92));
  }

  // Helper methods for growth styling
  getOrderGrowthClass(): string {
    const growth = this.getOrderGrowthPercentage();
    if (growth > 0) return 'positive';
    if (growth < 0) return 'negative';
    return 'neutral';
  }

  getOrderGrowthIcon(): string {
    const growth = this.getOrderGrowthPercentage();
    if (growth > 0) return 'fas fa-arrow-up';
    if (growth < 0) return 'fas fa-arrow-down';
    return 'fas fa-minus';
  }

  getRevenueGrowthClass(): string {
    const growth = this.getRevenueGrowthPercentage();
    if (growth > 0) return 'positive';
    if (growth < 0) return 'negative';
    return 'neutral';
  }

  getRevenueGrowthIcon(): string {
    const growth = this.getRevenueGrowthPercentage();
    if (growth > 0) return 'fas fa-arrow-up';
    if (growth < 0) return 'fas fa-arrow-down';
    return 'fas fa-minus';
  }

  getUserGrowthClass(): string {
    const growth = this.getUserGrowthPercentage();
    if (growth > 0) return 'positive';
    if (growth < 0) return 'negative';
    return 'neutral';
  }

  getUserGrowthIcon(): string {
    const growth = this.getUserGrowthPercentage();
    if (growth > 0) return 'fas fa-arrow-up';
    if (growth < 0) return 'fas fa-arrow-down';
    return 'fas fa-minus';
  }

  getCompletedOrdersClass(): string {
    const growth = this.getCompletedOrdersStatus();
    if (growth > 0) return 'positive';
    if (growth < 0) return 'negative';
    return 'neutral';
  }

  getCompletedOrdersIcon(): string {
    const growth = this.getCompletedOrdersStatus();
    if (growth > 0) return 'fas fa-arrow-up';
    if (growth < 0) return 'fas fa-arrow-down';
    return 'fas fa-minus';
  }

  private async getUserCount(): Promise<number> {
    try {
      const db = getFirestore();
      const usersCollection = collection(db, 'users');
      const snapshot = await getDocs(usersCollection);
      return snapshot.size;
    } catch (error) {
      console.error('Error getting user count:', error);
      return 0;
    }
  }
} 